import Vue from 'vue';
import { Loading, Notify, QSpinnerBars } from 'quasar';
import { Decimal } from 'decimal.js';
import VueEasySession from 'vue-easysession';
import axios from 'axios';
import moment from 'moment';
import VTooltip from 'v-tooltip';
import router from './router';
import IDKITApi from './libs/IDKITApi';
import Const from './const';
import ABtn from './components/ABtn.vue';
import AInput from './components/AInput.vue';
import AContainer from './components/AContainer.vue';
import ABreadcrumbs from './components/ABreadcrumbs.vue';
import AToggle from './components/AToggle.vue';
import ALoader from './components/ALoader.vue';
import BoxIcon from './components/BoxIcon.vue';
import AIcons from './components/AIcons.vue';
import AMoney from './components/AMoney.vue';
import './quasar';
import 'moment/locale/es';

Vue.config.productionTip = false;

moment.updateLocale('es', {
  months: [
    'Enero',
    'Febrero',
    'Marzo',
    'Abril',
    'Mayo',
    'Junio',
    'Julio',
    'Agosto',
    'Septiembre',
    'Octubre',
    'Noviembre',
    'Diciembre',
  ],
  monthsShort: ['Ene.', 'Feb.', 'Mar.', 'Abr.', 'May.', 'Jun.', 'Jul.', 'Aug.', 'Sep.', 'Oct.', 'Nov.', 'Dic.'],
});

/*
|--------------------------------------------------------------------------
| Registro de componentes globales para la aplicación
|--------------------------------------------------------------------------|
*/
Vue.component('ABtn', ABtn);
Vue.component('AInput', AInput);
Vue.component('AContainer', AContainer);
Vue.component('ABreadcrumbs', ABreadcrumbs);
Vue.component('AToggle', AToggle);
Vue.component('AMoney', AMoney);
Vue.component('AIcons', AIcons);
Vue.component('ALoader', ALoader);
Vue.component('BoxIcon', BoxIcon);

/*
|--------------------------------------------------------------------------
| Registro de librerias globales para la aplicación
|--------------------------------------------------------------------------
*/
Vue.use(VTooltip);
Vue.use(VueEasySession.install, {
  persist: true,
  expireSessionCallback() {
    return localStorage.getItem('isInternal') === '0'
      ? router.push({
        name: process.env.NODE_ENV !== 'production' ? 'loginDev' : 'loginClient',
        query: { orderId: this.orderId },
      })
      : router.push({ name: 'loginGrp' });
  },
});
const { CancelToken } = axios;
const alertShow = (mensaje, tipo = '', position = 'top') => {
  const conf = {
    message: mensaje,
    position,
    timeout: 25000 * 23,
    color: 'white',
  };

  if (typeof Vue.prototype.ultimaNotificacion === 'function') {
    Vue.prototype.ultimaNotificacion();
  }

  switch (tipo) {
    case 'success':
      conf.textColor = 'dark';
      conf.message = `<i aria-hidden="true" style="font-size: 24px; margin-right: 10px; color: #23b782" role="img" class="material-icons q-icon notranslate">check_circle</i>${
        conf.message}`;
      break;
    case 'error':
      conf.textColor = 'negative';
      conf.message = `<i aria-hidden="true" style="font-size: 24px; margin-right: 10px; color: #ff5c5c" role="img" class="material-icons q-icon notranslate">warning</i>${
        conf.message}`;
      break;
    case 'warning':
      conf.textColor = 'secondary';
      conf.message = `<i aria-hidden="true" style="font-size: 24px; margin-right: 10px; color: #fd9a04" role="img" class="material-icons q-icon notranslate">warning</i>${
        conf.message}`;
      break;
    default:
      conf.textColor = 'dark';
      conf.message = `<i aria-hidden="true" style="font-size: 24px; margin-right: 10px; color: #7033ff" role="img" class="material-icons q-icon notranslate">error</i>${
        conf.message}`;
  }

  Vue.prototype.ultimaNotificacion = Notify.create({
    ...conf,
    html: true,
    actions: [{ icon: 'close', color: 'info' }],
  });
};

const validateResponseErrorMessage = (error) => {
  const response = error?.response;
  const orderId = Vue.prototype.$session.get('orderIdDMS');
  const routeRedirect = process.env.NODE_ENV !== 'production' ? 'loginDev' : 'loginClient';
  let message = 'Problema de red, intente nuevamente.';

  if (response) {
    // Si hay un error de que no hay sesión de IDKIT redireccionamos al login con la orden en sesión
    if (response.status === 401) {
      message = 'Sesión expirada';
      Vue.prototype.$session.destroy();
      if (localStorage.getItem('isInternal') === '0') {
        router.push({ name: routeRedirect, query: { orderId } });
      } else {
        router.push({ name: 'loginGrp' });
      }
    } else {
      message = 'No pudimos comunicarnos con el servicio. Intente nuevamente más tarde.';
    }
  }

  return message;
};

const constUrlApp = () => {
  let urle = '';
  if (localStorage.getItem('isInternal') === '1') {
    urle = process.env.VUE_APP_API_GRP_URL;
  } else {
    urle = process.env.VUE_APP_API_URL;
  }
  return urle;
};

const setSessionToken = (tokenData) => {
  Vue.prototype.$session.set('token', tokenData.access_token);
  Vue.prototype.$session.set(
    'expire_date',
    moment()
      .add(tokenData.expires_in, 'seconds')
      .format('YYYY-MM-DD HH:mm'),
  );
};

Object.defineProperties(Vue.prototype, {
  moment: { get: () => moment },
  axiosCatch: {
    get: () => (error, callback) => {
      if (!axios.isCancel(error) && typeof callback === 'function') {
        callback(error);
      }
    },
  },
  axiosCancelToken: { get: () => CancelToken },
  axios: {
    get: () => {
      const http = axios.create({
        baseURL: constUrlApp(),
        headers: {
          'X-API-KEY': process.env.VUE_APP_API_KEY,
          Authorization: `Bearer ${Vue.prototype.$session.get('token')}`,
        },
      });

      http.interceptors.response.use(
        (response) => response,
        async (error) => {
          const originalRequest = error?.config || {};
          // Si l sesión expiró renovamos el token y continuamos con el request original
          if (error?.response?.status === 403 && !originalRequest._retry) {
            try {
              originalRequest._retry = true;
              // Actualizamos el token
              let tokenData;
              if (localStorage.getItem('isInternal') === '0') {
                // Token de IDKITAPI
                tokenData = await IDKITApi.updateSessionToken(Vue.prototype.$session.get('state'));
              } else {
                // Token de GRP
                tokenData = Vue.prototype.$session.get('token');
              }
              // Lo agregamos al request original
              originalRequest.headers.Authorization = localStorage.getItem('isInternal') === '0' ? `Bearer ${tokenData.access_token}` : `Bearer ${tokenData}`;
              // Actualizamos el token en sesión
              if (localStorage.getItem('isInternal') === '0') setSessionToken(tokenData);
              return http(originalRequest);
            } catch (updateErr) {
              this.error = validateResponseErrorMessage(updateErr);
            }
          } else {
            this.error = validateResponseErrorMessage(error);
          }

          return Promise.reject(error);
        },
      );

      return http;
    },
  },
  Decimal: { get: () => Decimal },
  alertShow: { get: () => alertShow },
  loaderShow: {
    get: () => (mensaje = '', color = 'white') => {
      document.onkeydown = () => false;
      Loading.show({
        spinner: QSpinnerBars,
        message: mensaje,
        messageColor: 'white',
        spinnerSize: 80,
        spinnerColor: color,
      });
    },
  },
  loaderHide: {
    get: () => () => {
      Loading.hide();
      document.onkeydown = () => true;
    },
  },
  Const: { get: () => Const },
  validateResponseErrorMessage: { get: () => (error) => validateResponseErrorMessage(error) },
  setSessionToken: {
    get: setSessionToken,
  },
});

/*
|--------------------------------------------------------------------------
| Registro de directivas
|--------------------------------------------------------------------------
*/

const validationEnter = (special, e) => special.indexOf(e.keyCode) !== -1
        // Ctrl+A
        || (e.keyCode === 65 && e.ctrlKey === true)
        // Ctrl+C
        || (e.keyCode === 67 && e.ctrlKey === true)
        // Ctrl+X
        || (e.keyCode === 88 && e.ctrlKey === true)
        // home, end, left, right
        || (e.keyCode >= 35 && e.keyCode <= 39);

Vue.directive('ingresar', {
  bind(el, binding) {
    el.addEventListener('keydown', (e) => {
      // Se omiten las teclas del, espacio, tab, esc y enter
      const special = [46, 8, 9, 27, 13];
      if (binding.modifiers.decimales) {
        // Si existe teclado numerico agregamos sus teclas
        special.push(110, 190);
      }
      // Aceptamos combinaciones de teclas basicas sobre inputs
      if (validationEnter(special, e)) {
        return;
      }
      if (
        binding.modifiers.letras
        // Solo letras
        && e.keyCode >= 65
        && e.keyCode <= 90
      ) {
        return;
      }
      if (
        binding.modifiers.numeros
        // Solo numeros enteros
        && ((!e.shiftKey && e.keyCode >= 48 && e.keyCode <= 57)
          // Si existe teclado numerico agregamos sus teclas
          || (e.keyCode >= 96 && e.keyCode <= 105))
      ) {
        return;
      }
      e.preventDefault();
    });
  },
});

export { Vue, router };
